import React from 'react'
import { Col, Form, Input, Row, Tooltip } from 'antd'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EXCEPTION_STATUS, REASON_MAPPING } from '../constants'
import { formatAmountValue } from '../utils'

interface Props {
  control: any
  exceptionStatus: string
  hasIssuedCheckFile: boolean
}

function CheckException(props: Props) {
  const { control, exceptionStatus, hasIssuedCheckFile } = props
  const { t } = useTranslation()

  const reasonCodes = useWatch({ control, name: 'reasons' });
  const reason = Array.isArray(reasonCodes)
    ? reasonCodes.map((code: string) => REASON_MAPPING[code] || code).join(', ')
    : reasonCodes;
  const otherReason = useWatch({ control, name: 'otherReason' });

  const finalReason = otherReason === null || otherReason === "" ? reason : otherReason;

  return (
    <>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.accountName')}>
            <Controller
              name='accountName'
              control={control}
              render={({ field }) => <Input {...field} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.check')}>
            <Controller
              name='checkNumber'
              control={control}
              render={({ field }) => <Input {...field} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.issuedAmount')}
          className={!hasIssuedCheckFile ? 'isRequired' : ''}>
            <Controller
              name='issuedAmount'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className='ant-input-view-only'
                  value= {hasIssuedCheckFile? `${field.value === 0 ? '' : formatAmountValue(field.value)}` : ''}
                />
              )}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.issuedPayee')}
          className={!hasIssuedCheckFile ? 'isRequired' : ''}>
            <Controller
              name='issuedPayee'
              control={control}
              render={({ field }) => <Input {...field}  value={hasIssuedCheckFile ? field.value : ''} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.issuedDate')} 
          className={!hasIssuedCheckFile ? 'isRequired' : ''}>          
            <Controller
              name='issuedDate'
              control={control}
              render={({ field }) => <Input {...field}  value={hasIssuedCheckFile ? field.value : ''} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.paidDate')}>
            <Controller
              name='paidDate'
              control={control}
              render={({ field }) => <Input {...field} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          {exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.PAID ||
            exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.RETURNED ? (
            <Form.Item label={t('QuickExceptionProcessing:label.decision')}>
              <Controller
                name='decision'
                control={control}
                render={({ field }) => <Input {...field} className='ant-input-view-only' />}
                disabled
              />
            </Form.Item>
          ) : (
            <Form.Item label={t('QuickExceptionProcessing:label.defaultDecision')}>
              <Controller
                name='defaultDecision'
                control={control}
                render={({ field }) => <Input {...field} className='ant-input-view-only' />}
                disabled
              />
            </Form.Item>
          )}
        </Col>
        <Col span={8}>
          {exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.PAID ||
            exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.RETURNED ? (
            <Form.Item label={t('QuickExceptionProcessing:label.decisionTime')}>
              <Controller
                name='decisionTime'
                control={control}
                render={({ field }) => (
                  <Tooltip placement='right' title={field?.value} color='white' trigger='hover' arrow={false}>
                    <Input {...field} className='ant-input-view-only long-text' />
                  </Tooltip>
                )}
                disabled
              />
            </Form.Item>
          ) : (
            <Form.Item label={t('QuickExceptionProcessing:label.decisionDueDate')}>
              <Controller
                name='decisionDueDate'
                control={control}
                render={({ field }) => (
                  <Tooltip placement='right' title={field?.value} color='white' trigger='hover' arrow={false}>
                    <Input {...field} className='ant-input-view-only long-text' />
                  </Tooltip>
                )}
                disabled
              />
            </Form.Item>
          )}
        </Col>
        <Col span={8}>
          <Form.Item label={t('QuickExceptionProcessing:label.cutoffTime')}>
            <Controller
              name='cutoffTime'
              control={control}
              render={({ field }) => <Input {...field} className='ant-input-view-only' />}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>
      {(exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.PAID ||
        exceptionStatus?.toLowerCase() === EXCEPTION_STATUS.RETURNED) && (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label={t('QuickExceptionProcessing:label.reason')}>
                <Controller
                  name='reasons'
                  control={control}
                  render={({ field }) => <Input {...field} value={finalReason} className='ant-input-view-only' />}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('QuickExceptionProcessing:label.decisionedBy')}>
                <Controller
                  name='decisionedBy'
                  control={control}
                  render={({ field }) => <Input {...field} className='ant-input-view-only' />}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {!hasIssuedCheckFile && (
        <div className="text-gray-500 mt-2">
          *If field is blank, an issued check or check file was not submitted.
        </div>
        )}
    </>
  )
}

export default CheckException
